import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RotateCcw } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { storytellerRuntime, watchMediaPlayback } from "../storyteller/runtime";
export function StorytellerClip({ clip }) {
    const reactId = useId();
    const mountId = useMemo(() => `storyteller-clip-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`, [reactId]);
    const mountRef = useRef(null);
    const [loadState, setLoadState] = useState("loading");
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let disposed = false;
        let clipHandle = null;
        let stopWatchingMedia = () => { };
        const focusBeforeClipMount = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        setLoadState("loading");
        void storytellerRuntime
            .createEmbeddedClip(mountId, clip.clipId, {
            onDataLoadStarted: () => {
                if (!disposed) {
                    setLoadState("loading");
                }
            },
            onDataLoadComplete: (success, _error, dataCount) => {
                if (!disposed) {
                    setLoadState(success && dataCount > 0 ? "ready" : "error");
                }
            }
        })
            .then((handle) => {
            if (disposed) {
                handle.dispose();
                return;
            }
            clipHandle = handle;
            if (mountRef.current) {
                stopWatchingMedia = makeEmbeddedMediaSafe(mountRef.current, focusBeforeClipMount);
            }
        })
            .catch(() => {
            if (!disposed) {
                setLoadState("error");
            }
        });
        return () => {
            disposed = true;
            stopWatchingMedia();
            clipHandle?.dispose();
        };
    }, [attempt, clip.clipId, mountId]);
    return (_jsxs("div", { className: "storyteller-clip", "data-storyteller-content": "clip", "data-load-state": loadState, children: [clip.poster ? (_jsx("img", { className: "storyteller-clip__poster", src: clip.poster, alt: "", "aria-hidden": "true" })) : null, _jsx("div", { id: mountId, ref: mountRef, className: "storyteller-clip__mount", "aria-label": clip.accessibleLabel }), loadState === "loading" ? (_jsxs("div", { className: "storyteller-clip__status", role: "status", children: [_jsx("span", { className: "storyteller-clip__spinner", "aria-hidden": "true" }), _jsx("span", { children: "Loading video" })] })) : null, loadState === "error" ? (_jsxs("div", { className: "storyteller-clip__status storyteller-clip__status--error", children: [_jsx("span", { children: "Video unavailable" }), _jsxs("button", { type: "button", onClick: () => setAttempt((value) => value + 1), children: [_jsx(RotateCcw, { "aria-hidden": "true" }), "Try again"] })] })) : null] }));
}
function makeEmbeddedMediaSafe(mount, focusBeforeClipMount) {
    const observedMedia = new Map();
    let pointerEngaged = false;
    let keyboardNavigation = false;
    let keyboardReset;
    const restoreUnexpectedFocus = () => {
        if (pointerEngaged ||
            keyboardNavigation ||
            !mount.contains(document.activeElement)) {
            return;
        }
        const activeElement = document.activeElement;
        if (!(activeElement instanceof HTMLElement)) {
            return;
        }
        if (focusBeforeClipMount &&
            focusBeforeClipMount !== document.body &&
            focusBeforeClipMount.isConnected) {
            focusBeforeClipMount.focus({ preventScroll: true });
            return;
        }
        activeElement.blur();
    };
    const onPointerDown = () => {
        pointerEngaged = true;
    };
    const onDocumentKeyDown = (event) => {
        if (event.key !== "Tab") {
            return;
        }
        keyboardNavigation = true;
        window.clearTimeout(keyboardReset);
        keyboardReset = window.setTimeout(() => {
            keyboardNavigation = false;
        }, 0);
    };
    const onFocusIn = () => {
        queueMicrotask(restoreUnexpectedFocus);
    };
    mount.addEventListener("pointerdown", onPointerDown, true);
    mount.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("keydown", onDocumentKeyDown, true);
    restoreUnexpectedFocus();
    const prepareMedia = (media) => {
        if (observedMedia.has(media)) {
            return;
        }
        media.defaultMuted = true;
        media.muted = true;
        if (media instanceof HTMLVideoElement) {
            media.playsInline = true;
            media.setAttribute("playsinline", "");
        }
        observedMedia.set(media, watchMediaPlayback(media));
    };
    const prepareCurrentMedia = () => {
        for (const media of mount.querySelectorAll("video, audio")) {
            prepareMedia(media);
        }
    };
    prepareCurrentMedia();
    const observer = new MutationObserver(prepareCurrentMedia);
    observer.observe(mount, { childList: true, subtree: true });
    return () => {
        observer.disconnect();
        window.clearTimeout(keyboardReset);
        mount.removeEventListener("pointerdown", onPointerDown, true);
        mount.removeEventListener("focusin", onFocusIn, true);
        document.removeEventListener("keydown", onDocumentKeyDown, true);
        for (const [media, removeListener] of observedMedia) {
            media.pause();
            removeListener();
        }
        observedMedia.clear();
    };
}
